import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PanelLeftClose,
  SquareCheckBig, // For Data Contracts
  Package,     // For Starter Kits
  Settings2,   // For Settings
  Globe, // For Schema Enforcement
  FileChartColumn, // For Chrome Extension (renamed from Extension)
} from "lucide-react";
import { Monitoring, Destinations, Pipelines } from './icons';
import ToolSwitcher from "./ToolSwitcher";
import { useAuth } from "../hooks/useAuth";
import { useGenericPersistence } from "../hooks/useGenericPersistence";
import clsx from "clsx";

// Reusable styling definition for menu items
interface NavLinkProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isCollapsed: boolean;
  isActive: boolean;
  isDisabled?: boolean; // Added isDisabled prop
  onClick: () => void;
}

const NavLink: React.FC<NavLinkProps> = ({ to, icon: Icon, label, isCollapsed, isActive, isDisabled = false, onClick }) => {
  const baseClasses = "flex items-center py-2 rounded-md text-sm font-medium transition-colors duration-200"; // Removed padding/margin
  const activeClasses = "bg-blue-600 text-white";
  const inactiveClasses = "text-gray-300 hover:bg-gray-700 hover:text-white";
  const disabledClasses = "text-gray-500 cursor-not-allowed opacity-50";

  return (
    <Link
      to={to}
      onClick={isDisabled ? (e) => e.preventDefault() : onClick}
      className={clsx(
        baseClasses,
        isActive ? activeClasses : inactiveClasses,
        isDisabled && disabledClasses,
        isCollapsed ? "px-3 justify-center" : "pr-4 pl-3 ml-1 mr-2" // Conditional padding/margin and justify
      )}
    >
      <Icon className={clsx("h-5 w-5", {"mr-2": !isCollapsed})} />
      {!isCollapsed && label}
    </Link>
  );
};

interface NavSectionProps {
  title: string;
  children: React.ReactNode;
  isCollapsed: boolean;
  defaultOpen?: boolean;
}

const NavSection: React.FC<NavSectionProps> = ({ title, children, isCollapsed, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleOpen = () => {
    if (!isCollapsed) { // Only allow toggling if not in collapsed state
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="space-y-2">
      <motion.button
        onClick={toggleOpen}
        className={clsx(
          "flex items-center w-full py-2 pl-4 rounded-md transition-colors duration-200",
          "hover:bg-gray-800", // Hover background for interactivity
          isCollapsed ? "justify-center" : "justify-between",
          "tracking-wider uppercase text-xs text-gray-500" // Section label styling
        )}
        whileHover={{ backgroundColor: isCollapsed ? "#1F2937" : undefined }} // Subtle hover for collapsed state
      >
        {!isCollapsed && <span>{title}</span>}
        {!isCollapsed && (
          <motion.div
            initial={false}
            animate={{ rotate: isOpen ? 0 : -90 }}
            transition={{ duration: 0.15 }} // Quicker animation
          >
          </motion.div>
        )}
      </motion.button>
      <AnimatePresence initial={false}>
        {isOpen && !isCollapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }} // Quicker animation, with collapse
            className="overflow-hidden space-y-3"
          >
            {children}
          </motion.div>
        )}
        {isCollapsed && (
          <div className="flex flex-col items-center space-y-2">
            {children}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface LabsNavbarProps {
  isMobileNavOpen: boolean;
  setMobileNavOpen: (isOpen: boolean) => void;
}

export default function LabsNavbar({ isMobileNavOpen, setMobileNavOpen }: LabsNavbarProps) {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768); // Tailwind's 'md' breakpoint
  
  // Use persistence for navbar collapsed state
  const { loadState, saveState } = useGenericPersistence<boolean>('labs-navbar-collapsed');
  
  // Initialize collapsed state from localStorage or default to false
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = loadState();
    return savedState !== null ? savedState : false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleNavbar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    saveState(newCollapsedState);
  };

  const navSections = [
    {
      title: "Pipelines",
      links: [
        { to: "/labs/customer-schemas", icon: Globe, label: "Customer Schemas", isDisabled: false },
        { to: "/labs/playbooks", icon: Pipelines, label: "Pipeline Transforms", isDisabled: false },
        { to: "/labs/pipeline-schemas", icon: FileChartColumn, label: "Pipeline Schemas", isDisabled: false },
      ],
    },
    {
      title: "Integrations",
      links: [
        { to: "/labs/integrations", icon: Destinations, label: "Playbook Editor", isDisabled: false },
        { to: "/labs/vendor-schemas", icon: Package, label: "Vendor Schemas", isDisabled: false },
      ],
    },
    {
      title: "MetaRouter Tools",
      links: [
        { to: "/labs/chrome-extension", icon: Monitoring, label: "Website Scans", isDisabled: false },
        { to: "/labs/contracts", icon: SquareCheckBig, label: "Data Contracts", isDisabled: false },
        { to: "/labs/settings/integrations", icon: Settings2, label: "Settings", isDisabled: false },
      ],
    },
  ];

  // Create a string className instead of using clsx to avoid TypeScript errors
  let navbarClassName = "fixed md:sticky top-0 z-20 md:z-10 flex flex-col bg-gray-900 dark:bg-gray-950 text-white transition-all dark:border-r dark:border-gray-700 duration-300 ease-in-out h-screen";
  
  // Add conditional classes
  if (isCollapsed) {
    navbarClassName += " w-20 items-center";
  } else {
    navbarClassName += " w-54";
  }
  
  // Add mobile visibility class
  if (isMobile && !isMobileNavOpen) {
    navbarClassName += " hidden md:flex";
  }

  return (
    <motion.div
      initial={false}
      animate={{ x: isMobile && !isMobileNavOpen ? "-100%" : "0%" }}
      transition={{ duration: 0.2, ease: "easeInOut" }} // Quicker animation
      className={navbarClassName}
    >
      {/* Top section - Logo and Tool Switcher */}
      <div className="flex items-center px-5 py-6">
        <ToolSwitcher isAdmin={isAdmin} isCollapsed={isCollapsed} />
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 px-2 py-4 space-y-10"> {/* Space sections 40px apart */}
        {navSections.map((section, index) => (
          <NavSection
            key={index}
            title={section.title}
            isCollapsed={isCollapsed}
            defaultOpen={true} // All sections open by default
          >
            {section.links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                icon={link.icon}
                label={link.label}
                isCollapsed={isCollapsed}
                isActive={location.pathname.startsWith(link.to)}
                isDisabled={link.isDisabled}
                onClick={() => {
                  if (isMobile) setMobileNavOpen(false); // Close mobile nav on link click
                }}
              />
            ))}
          </NavSection>
        ))}
      </nav>

      {/* Collapse/Expand Button */}
      <div className={`p-4 ${isCollapsed ? 'justify-center' : 'justify-end'} flex`}>
        <button
          onClick={toggleNavbar}
          className="p-2 rounded-md hover:bg-gray-700 text-gray-300 transition-colors duration-200">
          <PanelLeftClose className={`h-5 w-5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </motion.div>
  );
}
